/**
 * Analytics data model for tracking user behavior and preferences
 * Follows modular design principles with clear separation of concerns
 */

export interface TimeDistribution {
  morning: number;   // 5:00 AM - 11:59 AM
  afternoon: number; // 12:00 PM - 4:59 PM
  evening: number;   // 5:00 PM - 8:59 PM
  night: number;     // 9:00 PM - 4:59 AM
}

export interface DayOfWeekDistribution {
  sunday: number;
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  saturday: number;
}

export interface AnalyticsData {
  // App usage metrics
  firstVisitTimestamp: string;
  lastVisitTimestamp: string;
  currentAppVersion: string;
  lastSeenAppVersion: string;
  updateNotificationShown: boolean;
  totalVisits: number;
  
  // Time-based distribution of visits
  visitsPerTimeOfDay: TimeDistribution;
  visitsPerDayOfWeek: DayOfWeekDistribution;
  visitsPerWeek: Record<string, number>; // ISO week number (YYYY-WXX) as key
  visitsPerMonth: Record<string, number>; // YYYY-MM as key
  visitsPerYear: Record<string, number>; // YYYY as key
  
  // Medication usage metrics
  totalMedicationsAdded: number;
  totalMedicationsArchived: number;
  totalMedicationsTaken: number;
  totalMedicationsSkipped: number;
  
  // Feature usage metrics
  featureUsage: {
    // Page/View navigation
    monthlyViewOpened: number;
    weeklyViewOpened: number;
    dailyViewOpened: number;
    analyticsDashboardViewed: number;
    settingsOpened: number;
    
    // Medication actions
    medicationDetailsViewed: number;
    medicationAdded: number;
    medicationEdited: number;
    medicationDeleted: number;
    adherenceLogged: number;
    
    // Reminders
    reminderAcknowledged: number;
    reminderDismissed: number;
    reminderTimeChanged: number;
    reminderSent: number;
    reminderClicked: number;
    
    // Data actions
    exportDataUsed: number;
    importDataUsed: number;
    
    // User interface actions
    themeChanged: number;
    notificationsToggled: number;
    viewPreferenceChanged: number;
    
    // Other features
    calendarViewed: number;
    searchUsed: number;
    filterUsed: number;
    printSchedule: number;
    shareSchedule: number;
    tutorialViewed: number;
    helpOpened: number;
    
    // Plus tier features
    medicationSharing: number;
    helperManagement: number;
    customCategories: number;
    advancedReporting: number;
    dataSynchronization: number;
  };
  
  // Preference metrics
  preferences: {
    preferredView: 'daily' | 'weekly' | 'monthly' | null;
    preferredTimeFormat: '12h' | '24h' | null;
    notificationsEnabled: boolean | null;
    theme: 'light' | 'dark' | null;
    fontSize: 'small' | 'medium' | 'large' | null;
    reminderTime: string | null;
  };
  
  // Session metrics
  averageSessionDurationSeconds: number;
  sessionsData: {
    timestamp: string;
    durationSeconds: number;
    actionsPerformed: number;
  }[];
  
  // Enhanced Plus tier feature tracking
  plusFeatureDetails?: {
    [feature: string]: {
      totalUsage: number;
      lastUsed: string | null;
      subFeatures: {
        [subFeature: string]: {
          usageCount: number;
          lastUsed: string | null;
        };
      };
      recentContext?: Record<string, any>;
    };
  };
  
  // Custom event tracking
  customEvents?: Record<string, number>;
  
  // Plus tier subscription metrics
  plusSubscriptionData?: {
    subscriptionStartDate: string | null;
    currentPlanType: 'monthly' | 'annual' | null;
    totalRenewals: number;
    featureEngagement: {
      [feature: string]: number; // percentage of engagement with Plus features
    };
  };
}

/**
 * Default values for new analytics data instance
 */
export const createDefaultAnalyticsData = (): AnalyticsData => {
  const now = new Date().toISOString();
  return {
    firstVisitTimestamp: now,
    lastVisitTimestamp: now,
    currentAppVersion: "1.0.0",
    lastSeenAppVersion: "1.0.0",
    updateNotificationShown: false,
    totalVisits: 1,
    
    visitsPerTimeOfDay: {
      morning: 0,
      afternoon: 0,
      evening: 0,
      night: 0
    },
    
    visitsPerDayOfWeek: {
      sunday: 0,
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0
    },
    
    visitsPerWeek: {},
    visitsPerMonth: {},
    visitsPerYear: {},
    
    totalMedicationsAdded: 0,
    totalMedicationsArchived: 0,
    totalMedicationsTaken: 0,
    totalMedicationsSkipped: 0,
    
    featureUsage: {
      // Page/View navigation
      monthlyViewOpened: 0,
      weeklyViewOpened: 0,
      dailyViewOpened: 0,
      analyticsDashboardViewed: 0,
      settingsOpened: 0,
      
      // Medication actions
      medicationDetailsViewed: 0,
      medicationAdded: 0,
      medicationEdited: 0,
      medicationDeleted: 0,
      adherenceLogged: 0,
      
      // Reminders
      reminderAcknowledged: 0,
      reminderDismissed: 0,
      reminderTimeChanged: 0,
      reminderSent: 0,
      reminderClicked: 0,
      
      // Data actions
      exportDataUsed: 0,
      importDataUsed: 0,
      
      // User interface actions
      themeChanged: 0,
      notificationsToggled: 0,
      viewPreferenceChanged: 0,
      
      // Other features
      calendarViewed: 0,
      searchUsed: 0,
      filterUsed: 0,
      printSchedule: 0,
      shareSchedule: 0,
      tutorialViewed: 0,
      helpOpened: 0,
      
      // Plus tier features
      medicationSharing: 0,
      helperManagement: 0,
      customCategories: 0,
      advancedReporting: 0,
      dataSynchronization: 0
    },
    
    preferences: {
      preferredView: null,
      preferredTimeFormat: null,
      notificationsEnabled: null,
      theme: null,
      fontSize: null,
      reminderTime: null
    },
    
    averageSessionDurationSeconds: 0,
    sessionsData: [],
    
    // Initialize custom event tracking
    customEvents: {},
    
    // Initialize Plus tier feature tracking
    plusFeatureDetails: {},
    
    // Initialize Plus tier subscription data
    plusSubscriptionData: {
      subscriptionStartDate: null,
      currentPlanType: null,
      totalRenewals: 0,
      featureEngagement: {}
    }
  };
};
